import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_PERCENT = 0.30;

// Simple pub/sub so all EstimatedFeesField instances stay in sync across pages
const listeners = new Set();
let cachedValue = null;
let inFlight = null;

function notifyAll(value) {
  cachedValue = value;
  listeners.forEach((cb) => {
    try { cb(value); } catch (_) { /* ignore */ }
  });
}

async function fetchOnce() {
  if (cachedValue !== null) return cachedValue;
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const res = await axios.get(`${API}/user/estimated-fees`);
      const val = Number(res.data?.estimated_fees_percent);
      const safe = Number.isFinite(val) ? val : DEFAULT_PERCENT;
      notifyAll(safe);
      return safe;
    } catch (_) {
      notifyAll(DEFAULT_PERCENT);
      return DEFAULT_PERCENT;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

/**
 * Read + update the user's stored `estimated_fees_percent` (backend user pref).
 * All hook instances share a single cached value and re-render together.
 * Returns { fees, setFees, loading, save } — `fees` is always a number in %.
 */
export function useEstimatedFees() {
  const [fees, setLocalFees] = useState(cachedValue ?? DEFAULT_PERCENT);
  const [loading, setLoading] = useState(cachedValue === null);

  useEffect(() => {
    const cb = (v) => setLocalFees(v);
    listeners.add(cb);
    if (cachedValue === null) {
      fetchOnce().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
    return () => listeners.delete(cb);
  }, []);

  const save = useCallback(async (nextRaw) => {
    const next = Math.max(0, Math.min(10, Number(nextRaw) || 0));
    notifyAll(next);  // optimistic
    try {
      const res = await axios.patch(`${API}/user/estimated-fees`, {
        estimated_fees_percent: next,
      });
      const persisted = Number(res.data?.estimated_fees_percent) || next;
      notifyAll(persisted);
      return persisted;
    } catch (_) {
      return next;
    }
  }, []);

  return { fees, setFees: setLocalFees, save, loading };
}

export const DEFAULT_FEES_PERCENT = DEFAULT_PERCENT;
